import styled, { DefaultTheme } from 'styled-components';
import { Table, Card, Input, Button, Modal, Typography, Statistic } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

const getThemeValue = <T,>(props: { theme?: DefaultTheme }, darkValue: T, lightValue: T): T => {
  return props.theme?.mode === 'dark' ? darkValue : lightValue;
};

export const Container = styled.div`
  padding: 16px 16px 16px 0px;
  background: ${(props) => getThemeValue(props, '#171923', 'transparent')};
  color: ${(props) => getThemeValue(props, '#fff', 'inherit')};
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
  color: ${(props) => getThemeValue(props, '#fff', 'inherit')};
`;

export const Title = styled(Typography.Title)`
  margin: 0 !important;
  color: ${(props) => getThemeValue(props, '#fff', undefined)} !important;
  font-weight: 600 !important;
`;

export const Subtitle = styled(Typography.Text)`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')};
  font-size: 14px;
  display: block;
  margin-top: 4px;
`;

export const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  margin-bottom: 24px;

  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

export const StatsCard = styled(Card)`
  border-radius: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 8px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};
  background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  transition: all 0.3s ease;

  &:hover {
    box-shadow: ${(props) =>
      getThemeValue(
        props,
        '0 4px 16px rgba(0, 0, 0, 0.4)',
        '0 4px 16px rgba(0, 0, 0, 0.15)'
      )};
  }

  .ant-card-body {
    padding: 24px;
  }
`;

export const StatsIconContainer = styled.div<{ bgColor: string }>`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${(props) => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  transition: all 0.3s ease;
`;

export const StyledStatistic = styled(Statistic)`
  .ant-statistic-title {
    color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')};
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 8px;
  }

  .ant-statistic-content {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
    font-size: 32px;
    font-weight: 700;
  }

  .ant-statistic-content-value {
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }
`;

export const StyledCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid ${(props) => getThemeValue(props, '#718096', '#DCDCDC')};
  margin-bottom: 24px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};

  .ant-card-body {
    padding: 12px 12px 12px 12px;
    border-radius: 12px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }
`;

export const TopControlBar = styled.div`
  display: flex;
  align-items: center;
  padding-bottom: 16px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#fff')};
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#718096', '#f0f0f0')};
  gap: 16px;
  flex-wrap: wrap;
`;

export const StyledInput = styled(Input.Search)`
  max-width: 100%;

  @media (min-width: 768px) {
    max-width: 60%;
  }

  @media (min-width: 1024px) {
    max-width: 50%;
  }

  .ant-input {
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-color: ${(props) => getThemeValue(props, '#4a5568', '#d9d9d9')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-input::placeholder {
    color: ${(props) => getThemeValue(props, '#718096', '#bfbfbf')};
  }
`;

export const AddButton = styled(Button)`
  background: #2D6CCA !important;
  border-color: #2D6CCA !important;
  color: white !important;
  height: 40px;
  border-radius: 8px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  transition: all 0.3s ease;

  &:hover {
    background: #2560b8 !important;
    color: white !important;
    box-shadow: 0 4px 12px rgba(45, 108, 202, 0.4);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`;

export const TableCard = styled(Card)`
  border-radius: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 8px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};
  background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  overflow: hidden;

  .ant-card-body {
    padding: 0;
  }
`;

export const TableHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const TableHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  font-size: 18px;
  font-weight: 600;
`;

export const StyledTable = styled(Table)`
  .ant-table {
    background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-table-thead > tr > th {
    background: ${(props) => getThemeValue(props, '#1a202c', '#fafafa')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
    font-weight: 600;
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
    text-align: center;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background: ${(props) => getThemeValue(props, '#2d3748', '#f0f0f0')};
    }
  }

  .ant-table-tbody > tr {
    transition: background-color 0.2s ease;

    &:hover {
      background: ${(props) => getThemeValue(props, '#2d3748', '#fafafa')};
    }
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
    text-align: center;
  }
`;

export const NameLink = styled.span`
  color: #14b8a6;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover {
    color: #0d9488;
    text-decoration: underline;
  }
`;

export const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  align-items: center;
`;

export const IconButton = styled(Button)`
  border: none;
  box-shadow: none;
  transition: all 0.2s ease;
`;

export const EmptyState = styled.div`
  padding: 48px 24px;
  text-align: center;
`;

export const EmptyText = styled.div`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#8c8c8c')};
  font-size: 16px;
  margin-top: 16px;
`;

export const EmptySubtext = styled.div`
  color: ${(props) => getThemeValue(props, '#718096', '#bfbfbf')};
  font-size: 14px;
  margin-top: 8px;
`;

export const StyledModal = styled(Modal)`
  .ant-modal-mask {
    z-index: 2000 !important;
    background-color: rgba(0, 0, 0, 0.45);
    backdrop-filter: blur(4px);
  }

  .ant-modal-content {
    border-radius: 16px;
    overflow: hidden;
    box-shadow: ${(props) => getThemeValue(props, '0 20px 60px rgba(0, 0, 0, 0.5)', '0 20px 60px rgba(0, 0, 0, 0.15)')};
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
  }

  .ant-modal-header {
    border-bottom: 1px solid ${(props) => getThemeValue(props, '#434343', '#f0f0f0')};
    padding: 20px 24px;
    background: ${(props) => getThemeValue(props, '#1a202c', 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)')};
    border-radius: 16px 16px 0 0;

    .ant-modal-title {
      font-size: 20px;
      font-weight: 600;
      color: ${(props) => getThemeValue(props, '#fff', '#1a1a1a')};
    }
  }

  .ant-modal-body {
    overflow-y: auto;
    padding: 24px;
    background: ${(props) => getThemeValue(props, '#1a202c', '#fafafa')};
    color: ${(props) => getThemeValue(props, '#ffffff', '#262626')};
  }

  .ant-modal-footer {
    border-top: 1px solid ${(props) => getThemeValue(props, '#434343', '#f0f0f0')};
    background: ${(props) => getThemeValue(props, '#1a202c', '#ffffff')};
    border-radius: 0 0 16px 16px;
  }

  .ant-modal-close {
    top: 20px;
    right: 24px;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    transition: all 0.2s;
    color: ${(props) => getThemeValue(props, '#fff', 'inherit')};

    &:hover {
      background-color: ${(props) => getThemeValue(props, '#4a5568', '#f5f5f5')};
    }
  }
`;

export const FormContainer = styled.div`
  padding: 16px;
  background: ${(props) => getThemeValue(props, '#1a202c', '#f5f5f5')};
`;

export const FormHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

export const FormContent = styled.div`
  max-width: 600px;
  margin: 0 auto;
  background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')};
  padding: 24px;
  border-radius: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 8px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};
`;

export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

export const LoadingText = styled.div`
  color: ${(props) => getThemeValue(props, '#a0aec0', '#666666')};
  font-size: 16px;
`;

export const StatHelpText = styled.div<{ color: string }>`
  color: ${(props) => props.color};
  font-size: 14px;
  margin-top: 8px;
`;

export const SkeletonCard = styled(Card)`
  border-radius: 12px;
  box-shadow: ${(props) =>
    getThemeValue(
      props,
      '0 2px 8px rgba(0, 0, 0, 0.3)',
      '0 2px 8px rgba(0, 0, 0, 0.1)'
    )};
  background: ${(props) => getThemeValue(props, '#2d3748', '#ffffff')};
  border: 1px solid ${(props) => getThemeValue(props, '#4a5568', '#e5e7eb')};

  .ant-card-body {
    padding: 24px;
  }
`;

export const SortableHeader = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
`;

export const EditIconButton = styled(IconButton)`
  color: #14b8a6;

  &:hover {
    color: #0d9488;
  }
`;

export const ModalNote = styled.p`
  margin-bottom: 16px;
`;

export const SaveButton = styled(Button)`
  background: #2D6CCA !important;
  background-color: #2D6CCA !important;
  color: white !important;
  border-radius: 8px !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  padding: 8px 16px !important;
  min-width: 80px;
  height: auto !important;

  &:hover {
    background: #2560b8 !important;
    background-color: #2560b8 !important;
    color: white !important;
  }

  &:disabled {
    background: #2D6CCA !important;
    background-color: #2D6CCA !important;
    opacity: 0.6;
  }
`;

export const ModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
`;

export const CancelButton = styled(Button)`
  background-color: #ededed !important;
  border: 0 !important;
  border-radius: 8px !important;
  color: #121212 !important;
  cursor: pointer !important;
  font-family: "Inter", sans-serif !important;
  font-size: 16px !important;
  font-weight: 500 !important;
  padding: 8px 16px !important;
  transition: all 0.2s ease !important;
  height: auto !important;

  &:hover {
    background: #e0e0e0 !important;
    background-color: #e0e0e0 !important;
    border: 0 !important;
    color: #121212 !important;
  }
`;
